import {
    Box,
    capitalize,
    Dialog,
    DialogContent,
    IconButton,
    Stack,
} from "@mui/material";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {GridColDef, GridRowId} from "@mui/x-data-grid";
import {
    DataLoader,
    DataRequest,
    DataRequestState,
    useDataRequest,
} from "../data";
import {useT} from "../../i18n";
import SearchField from "../SearchField";
import {useGetApi} from "../../client";
import {EingangsrechnungInfo} from "../../api";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {apiUrl} from "../../config";
import DataTable from "../data/DataTable";
import ZahllaufRechnungenModal from "./ZahllaufRechnungenModal";
import {formatAsMoney} from "../../formatterHelpers";
import useCustomFilters from "../data/useCustomFilters";

export type ZahllaufFilter = {
    search?: string;
    zahllauf?: string;
    zahllaufBezahlt?: boolean;
    filtered?: string;
};

export type Props = Omit<DataRequest<ZahllaufFilter>, "filter"> &
    Partial<Pick<DataRequest<ZahllaufFilter>, "filter">>;

const tableName: string = "zahllauf-archiv"

export default function ZahllaufArchivDataTable({...input}: Props) {
    const [filteredState, setFilteredState] = useState("");
    const request = useDataRequest<ZahllaufFilter>({
        filter: {zahllaufBezahlt: true},
        ...input,
    });

    return (
        <Stack spacing={2}>
            <FilterComp
                request={request}
                filteredStateFilter={filteredState}
            />
            <ZahllaufResults
                request={request}
                setFilteredState={setFilteredState}
            />
        </Stack>
    );
}

function FilterComp({
                        request,
                        filteredStateFilter,
                    }: {
    request: DataRequestState<ZahllaufFilter>;
    filteredStateFilter: string;
}) {
    const {setFilter} = request;
    const {currentCustomFilter, setCustomField} = useCustomFilters<ZahllaufFilter>({
        tableName: tableName,
        defaultFilter: {
            search: "",
            zahllauf: "",
            zahllaufBezahlt: request.filter.zahllaufBezahlt,
        }
    });

    useEffect(() => {
        setFilter({
            search: currentCustomFilter.search,
            zahllauf: currentCustomFilter.zahllauf,
            zahllaufBezahlt: currentCustomFilter.zahllaufBezahlt,
            filtered: filteredStateFilter || undefined,
        });
    }, [currentCustomFilter, filteredStateFilter, setFilter]);

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                position: "relative",
                width: "100%",
            }}
        >
            <SearchField
                value={currentCustomFilter.search}
                onChange={(val) => setCustomField("search", val)}
            />
        </Box>
    );
}

function ZahllaufResults({
                             request,
                             setFilteredState,
                         }: {
    request: DataRequestState<ZahllaufFilter>;
    setFilteredState: (value: string) => void;
}) {
    return <Results request={request} setFilteredState={setFilteredState}/>;
}

function Results({
                     request,
                     setFilteredState,
                 }: {
    request: DataRequestState<ZahllaufFilter>;
    setFilteredState: (value: string) => void;
}) {
    const getApi = useGetApi();

    const [zahllaufId, setZahllaufId] = useState<string>("");
    const [openedModal, setOpenedModal] = useState<boolean>(false);

    const loadEingangsrechnungen: DataLoader<ZahllaufFilter, EingangsrechnungInfo> = useCallback(
        async (params) => (await getApi()).zahllaeufe.sucheZahllaeufe({...params}),
        [getApi]
    );

    const handleDownloadZahlungsdatei = (zahllaufId: GridRowId) => {
        window.open(
            `${apiUrl}/zahllaeufe/${encodeURIComponent(zahllaufId)}/anhang`
        );
    };

    function handleOpenRechnungenModal(zahllaufId: string) {
        setOpenedModal(true);
        setZahllaufId(zahllaufId);
    }

    function handleCloseRechnungenModal() {
        setOpenedModal(false);
    }

    return (
        <>
            <DataTable
                columns={useColumns(handleDownloadZahlungsdatei)}
                request={request}
                queryKey={[tableName]}
                loadData={loadEingangsrechnungen}
                name={tableName}
                setFiltered={setFilteredState}
                //@ts-ignore
                onCellClick={(cell) => {
                    if (cell.field !== "download") {
                        handleOpenRechnungenModal(cell.id);
                    }
                }}
            />
            <Dialog
                title={"Zahllauf Rechnungen"}
                open={openedModal}
                onClose={handleCloseRechnungenModal}
                // @ts-ignore
                maxWidth={1}
                sx={{marginLeft: "20em"}}
                fullWidth={true}
            >
                <DialogContent>
                    <ZahllaufRechnungenModal
                        zahllaufId={zahllaufId}
                        setOpenedModal={setOpenedModal}
                    />
                </DialogContent>
            </Dialog>
        </>
    );
}

function useColumns(
    handleDownloadZahlungsdatei: (zahllaufId: GridRowId) => void
): Array<GridColDef<EingangsrechnungInfo>> {
    const {t: translation} = useT("zahllauf");

    return useMemo(
        () => [
            {
                field: "bezeichnung",
                headerName: capitalize(translation("description")),
                flex: 1,
                type: "string",
            },
            {
                field: "bezahltAm",
                headerName: capitalize(translation("paid-on")),
                flex: 1,
                valueGetter: (value) => {
                    return new Date(value);
                },
                renderCell: ({value}) => {
                    const date = new Date(value);
                    // @ts-ignore
                    return isNaN(date)
                        ? ""
                        : `${("0" + date.getDate()).slice(-2)}.${(
                            "0" +
                            (date.getMonth() + 1)
                        ).slice(-2)}.${date.getFullYear()}`;
                },
                type: "date",
            },
            {
                field: "anzahlRechnungen",
                headerName: capitalize(translation("invoice-count")),
                flex: 1,
                type: "string",
            },
            {
                field: "zahlbetrag",
                headerName: capitalize(translation("payment-amount")),
                flex: 1,
                align: "right",
                headerAlign: "right",
                type: "number",
                renderCell: ({value}) => {
                    return formatAsMoney(value);
                },
            },
            {
                field: "download",
                headerName: "",
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                width: 60,
                renderCell: (params) => (
                    <>
                        <IconButton
                            onClick={() => handleDownloadZahlungsdatei(params.row.id)}
                        >
                            <FileDownloadIcon/>
                        </IconButton>
                    </>
                ),
            },
        ],
        [handleDownloadZahlungsdatei, translation]
    );
}
